import { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Checkbox } from '@Components/FormCheck';
import { MdArrowBack, MdAutoAwesome, MdWarningAmber } from 'react-icons/md';
import { Button } from '../../../Components/Button';
import { Spinner } from '../../../Components/Spinner';
import { Appearance, Size } from '../../../Components/designConstants';
import { alexaImproveListing } from './ai-optimize-utils';
import { OptimizableFieldType, OptimizationResult } from './ai-optimize-constants';
import { AlexaAssessment, buildAssessment } from './alexa-assessments-utils';
import { AlexaAssessmentResult, assessAlexaReadiness } from './alexa-readiness-utils';
import { ProductProposal, SavedSuggestion } from './proposals-utils';
import { AuditedProduct } from './scoring-engine-constants';
import { formatAiError } from './title-compliance-utils';

const ALEXA_FIELD_LABEL: Record<OptimizableFieldType, string> = {
  title: 'Title',
  bullets: 'Feature Bullets',
  description: 'Description'
};

const scoreToneClass = (score: number): string => {
  if (score >= 80) return 'tone-success';
  if (score >= 60) return 'tone-brand';
  if (score >= 40) return 'tone-warning';
  return 'tone-danger';
};

interface AssessmentViewProps {
  loading: boolean;
  error: string | null;
  result: AlexaAssessmentResult | null;
  onImprove: () => void;
  onRetry: () => void;
}

const AssessmentView = ({ loading, error, result, onImprove, onRetry }: AssessmentViewProps) => {
  if (loading) {
    return (
      <div className='scorecard-modal-loading'>
        <Spinner size={40} />
        <p>Scoring Alexa for Shopping readiness...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className='scorecard-modal-error'>
        <MdWarningAmber />
        <p>{error}</p>
        <Button appearance={Appearance.OUTLINED} size={Size.SM} onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }
  if (!result) return null;

  return (
    <div className='alexa-assessment-view'>
      <div className='alexa-assessment-headline'>
        <span className={scoreToneClass(result.score)}>{result.score}%</span>
        <p>{result.summary}</p>
      </div>
      <div className='alexa-assessment-dimensions'>
        {result.dimensions.map(d => (
          <div key={d.key} className='alexa-assessment-dimension'>
            <div className='alexa-assessment-dimension-header'>
              <span>{d.label}</span>
              <span>{d.score}/25</span>
            </div>
            <div className='score-meter'>
              <div
                className={`score-meter-fill ${scoreToneClass((d.score / 25) * 100)}`}
                style={{ width: `${(d.score / 25) * 100}%` }}
              />
            </div>
            <p>{d.rationale}</p>
          </div>
        ))}
      </div>
      <Button size={Size.LG} leadingElement={<MdAutoAwesome />} onClick={onImprove}>
        Improve Weakest Dimensions
      </Button>
    </div>
  );
};

interface ImprovementViewProps {
  loading: boolean;
  error: string | null;
  result: OptimizationResult | null;
  accepted: Record<OptimizableFieldType, boolean>;
  onToggleAccept: (field: OptimizableFieldType) => void;
  onBack: () => void;
}

const ImprovementView = ({
  loading,
  error,
  result,
  accepted,
  onToggleAccept,
  onBack
}: ImprovementViewProps) => (
  <div className='alexa-improvement-view'>
    <Button
      appearance={Appearance.EMPTY}
      size={Size.SM}
      leadingElement={<MdArrowBack />}
      onClick={onBack}
    >
      Back to assessment
    </Button>
    {loading && (
      <div className='scorecard-modal-loading'>
        <Spinner size={40} />
        <p>Generating voice-search improvements...</p>
      </div>
    )}
    {error && (
      <div className='scorecard-modal-error'>
        <MdWarningAmber />
        <p>{error}</p>
      </div>
    )}
    {!loading &&
      !error &&
      result &&
      (['title', 'bullets', 'description'] as OptimizableFieldType[]).map(field => {
        const suggestion = result[field];
        if (!suggestion) return null;
        return (
          <div key={field} className='optimize-field-panel'>
            <div className='optimize-field-panel-header'>
              <span>{ALEXA_FIELD_LABEL[field]}</span>
              <Checkbox
                label='Accept rewrite'
                checked={accepted[field]}
                onChange={() => onToggleAccept(field)}
              />
            </div>
            <p>{suggestion.suggestion}</p>
            <p className='optimize-reasoning'>{suggestion.reasoning}</p>
          </div>
        );
      })}
  </div>
);

interface AlexaReadinessModalProps {
  product: AuditedProduct;
  existingAssessment?: AlexaAssessment | null;
  existingProposal?: ProductProposal;
  onSaveAssessment: (productId: string, assessment: AlexaAssessment | null) => void;
  onSaveProposal: (productId: string, proposal: ProductProposal | null) => void;
  onClose: () => void;
}

export const AlexaReadinessModal = ({
  product,
  existingAssessment,
  existingProposal,
  onSaveAssessment,
  onSaveProposal,
  onClose
}: AlexaReadinessModalProps) => {
  const [loading, setLoading] = useState(!existingAssessment);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AlexaAssessmentResult | null>(existingAssessment ?? null);

  const [view, setView] = useState<'assessment' | 'improvement'>('assessment');
  const [improveLoading, setImproveLoading] = useState(false);
  const [improveError, setImproveError] = useState<string | null>(null);
  const [improveResult, setImproveResult] = useState<OptimizationResult | null>(null);
  const [improveAccepted, setImproveAccepted] = useState<Record<OptimizableFieldType, boolean>>({
    title: false,
    bullets: false,
    description: false
  });

  // Guards against setState after the modal has closed mid-request.
  const unmountedRef = useRef(false);
  useEffect(
    () => () => {
      unmountedRef.current = true;
    },
    []
  );

  const runAssessment = () => {
    setLoading(true);
    setError(null);
    assessAlexaReadiness(product)
      .then(res => {
        if (unmountedRef.current) return;
        setResult(res);
        setLoading(false);
        onSaveAssessment(product.id, buildAssessment(product, res));
      })
      .catch(err => {
        if (unmountedRef.current) return;
        setError(formatAiError(err));
        setLoading(false);
      });
  };

  useEffect(() => {
    if (existingAssessment) return;
    runAssessment();
    // Intentionally keyed on `product.id` only — re-running whenever `onSaveAssessment`
    // (a fresh closure from the parent on every render) changes would re-assess on
    // every keystroke elsewhere in the app, not just when the product being viewed changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  const runImprove = () => {
    if (!result) return;
    setView('improvement');
    setImproveLoading(true);
    setImproveError(null);
    alexaImproveListing(product, result)
      .then(res => {
        if (unmountedRef.current) return;
        setImproveResult(res);
        setImproveLoading(false);
      })
      .catch(err => {
        if (unmountedRef.current) return;
        setImproveError(formatAiError(err));
        setImproveLoading(false);
      });
  };

  const persistImprovements = () => {
    if (!improveResult) return;
    const buildSaved = (
      s: OptimizationResult['title'],
      isAccepted: boolean
    ): SavedSuggestion | undefined =>
      s ? { suggestion: s.suggestion, reasoning: s.reasoning, accepted: isAccepted } : undefined;
    onSaveProposal(product.id, {
      productId: product.id,
      generatedAt: existingProposal?.generatedAt ?? Date.now(),
      title: buildSaved(improveResult.title, improveAccepted.title) ?? existingProposal?.title,
      bullets:
        buildSaved(improveResult.bullets, improveAccepted.bullets) ?? existingProposal?.bullets,
      description:
        buildSaved(improveResult.description, improveAccepted.description) ??
        existingProposal?.description
    });
  };

  const handleClose = () => {
    persistImprovements();
    onClose();
  };

  return (
    <Modal show centered size='lg' onHide={handleClose} className='scorecard-alexa-modal'>
      <Modal.Header closeButton>
        <div>
          <span className='scorecard-modal-eyebrow'>Alexa for Shopping Readiness</span>
          <Modal.Title>{product.title}</Modal.Title>
          <span className='scorecard-modal-subtitle'>
            {product.asin} · {product.brand}
          </span>
        </div>
      </Modal.Header>
      <Modal.Body>
        {view === 'assessment' ? (
          <AssessmentView
            loading={loading}
            error={error}
            result={result}
            onImprove={runImprove}
            onRetry={runAssessment}
          />
        ) : (
          <ImprovementView
            loading={improveLoading}
            error={improveError}
            result={improveResult}
            accepted={improveAccepted}
            onToggleAccept={field =>
              setImproveAccepted(prev => ({ ...prev, [field]: !prev[field] }))
            }
            onBack={() => setView('assessment')}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleClose}>Save & Close</Button>
      </Modal.Footer>
    </Modal>
  );
};
